import type { ResumeChange } from "@/lib/api/resumeService";

/** What the tailor changed, as the What changed view lists it. */

export interface ChangeRow {
    kind: string;
    tone: "applied" | "raised" | "queued";
    what: string;
    before: string | null;
    after: string | null;
    note: string | null;
}

const SECTION_NAME: Record<ResumeChange["section"], string> = {
    HEADLINE: "Headline",
    SUMMARY: "Summary",
    SKILLS: "Skills",
    EXPERIENCE: "Experience",
    EDUCATION: "Education",
    CERTIFICATIONS: "Certifications",
};

function placeOf(change: ResumeChange): string {
    if (change.section === "EXPERIENCE" && change.role) {
        return `${change.role.title}, ${change.role.company}`;
    }
    return SECTION_NAME[change.section];
}

/** Text ended with a full stop, once. */
function sentence(words: string): string {
    return /[.!?]$/.test(words.trim()) ? words.trim() : `${words.trim()}.`;
}

/** One row a change, in the resume's order, as the What changed view lists them. */
export function changeRows(changes: readonly ResumeChange[]): ChangeRow[] {
    return changes.map((change) => {
        const place = placeOf(change);
        switch (change.kind) {
            case "REWORDED":
                return {
                    kind: "Reworded",
                    tone: "applied",
                    what: place,
                    before: change.before.join(" ") || null,
                    after: change.after.join(" ") || null,
                    note: null,
                };
            case "REORDERED":
                return {
                    kind: "Reordered",
                    tone: "raised",
                    what: `${place}, in a new order`,
                    before: null,
                    after: null,
                    note: `Now: ${sentence(change.after.join(" · "))} Was: ${sentence(change.before.join(" · "))}`,
                };
            case "DROPPED":
                return {
                    kind: "Dropped",
                    tone: "raised",
                    what: `${place}, left out`,
                    before: null,
                    after: null,
                    note: `${sentence(change.before.join(" · "))} It is still in your resume.`,
                };
            case "ADDED":
                return {
                    kind: "Added",
                    tone: "applied",
                    what: `${place}, added`,
                    before: null,
                    after: null,
                    note: change.after.join(" · ") || null,
                };
            case "UNCHANGED":
                return {
                    kind: "Unchanged",
                    tone: "queued",
                    what: `${place}, as it was`,
                    before: null,
                    after: null,
                    note: null,
                };
        }
    });
}
